import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { availabilityService } from '../services/availabilityService';
import type { BlockBody } from '../types';

import { availabilityKeys } from './useAvailabilityRange';

export type CopySourceBlock = Omit<BlockBody, 'date'>;

export interface CopyDayBlocksInput {
  sourceBlocks: CopySourceBlock[];
  targetDates: string[];
}

export interface CopyDayBlocksOutcome {
  copied: number;
  failed: { date: string; error: string }[];
}

export function useCopyDayBlocks(practitionerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      sourceBlocks,
      targetDates,
    }: CopyDayBlocksInput): Promise<CopyDayBlocksOutcome> => {
      const tasks = targetDates.flatMap((date) =>
        sourceBlocks.map(async (b) => {
          try {
            await availabilityService.createBlock(practitionerId, { ...b, date });
            return { ok: true as const, date };
          } catch (err) {
            const message = err instanceof Error ? err.message : 'Unknown error';
            return { ok: false as const, date, error: message };
          }
        }),
      );
      const results = await Promise.all(tasks);
      const failed = results
        .filter((r): r is { ok: false; date: string; error: string } => !r.ok)
        .map((r) => ({ date: r.date, error: r.error }));
      return { copied: results.length - failed.length, failed };
    },
    onSuccess: async (out) => {
      await qc.invalidateQueries({ queryKey: availabilityKeys.lists() });
      if (out.failed.length === 0) {
        toast.success(`Copied to ${String(out.copied)} block${out.copied === 1 ? '' : 's'}`);
      } else {
        toast.warning(
          `Copied ${String(out.copied)} block${out.copied === 1 ? '' : 's'}; ${String(out.failed.length)} failed`,
        );
      }
    },
  });
}
